import { GridColDef } from '@mui/x-data-grid';

import { capitalize } from 'lodash';
import { Observer } from 'mobx-react';
import * as React from 'react';
import { tokenDecimals } from '../../../helpers/config/contracts.config';
import {formatCurrencyAmount, formatPriceWithSign} from '../../../helpers/formatters';
import {
    calculatePositionSizeWithPosition,
    getNetPnlWithPosition
} from '../../../helpers/math/position-math.helpers';
import { IPositionResponse } from '../../../services/api/api.interfaces';
import CryptoIconPair from '../../CryptoIconPair/CryptoIconPair';
import NereusButton from '../../NereusButton/NereusButton';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import dayjs from "dayjs";
import StatusIcon from '../../StatusIcon/StatusIcon';

export function renderDateTime(dateTimeString: string) {
    const dateTime = dayjs(dateTimeString);
    const date = dateTime.format('YYYY.MM.DD');
    const time = dateTime.format('HH:mm:ss');

    return <div className="text-right"><span className="block">{date}</span><span
        className="block nereus-caption nereus-color-neutral-grey">{time}</span></div>;
}

const closedReasonFormat: { [key: string]: string } = {
    CLOSE_NOW: 'Manual',
    STOP_LOSS: 'SL',
    TAKE_PROFIT: 'TP',
    INSUFFICIENT_FUNDS: 'Liquidation'
}

export const baseAssetOverride = (baseAsset: string) => {
    if (baseAsset.toLowerCase() === 'trumpwins') {
        return 'TRUMP'
    } else if (baseAsset.toLowerCase() === 'harriswins') {
        return 'HARRIS'
    }

    return baseAsset
}

const columnConfig: Record<string, GridColDef<IPositionResponse>> = {
    market: {
        field: 'Market', headerName: 'Market',
        minWidth: 150, renderCell: (params) => {
            return (
                <div className="flex items-center">
                    <CryptoIconPair
                        variant="diagonal"
                        baseTicker={params.row.baseAsset}
                        quoteTicker={params.row.quoteAsset}/>

                    <div className="ml-3">
                        <span className="uppercase">{baseAssetOverride(params.row.baseAsset)}/{params.row.quoteAsset}</span>
                        {!params.row.isShort && <div className="nereus-color-status-up nereus-caption">
                            Long {params.row.leverage}x
                        </div>}
                        {params.row.isShort && <div className="nereus-color-status-down nereus-caption">
                            Short {params.row.leverage}x
                        </div>}
                    </div>

                </div>
            )
        }
    },
    startedAt: {
        flex: 1,
        minWidth: 100,
        field: 'startedAt', headerName: 'Open Date',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (params) => dayjs(params.row.startedAt).valueOf(),
        renderCell: (params) => renderDateTime(params.row.startedAt)
    },
    status: {
        flex: 1,
        field: 'status', headerName: 'Status',
        align: 'right',
        headerAlign: 'right',
        minWidth: 70,
        valueGetter: (params) => capitalize(params.row.status)
    },
    amount: {
        flex: 1,
        field: 'Amount', headerName: 'Amount',
        align: 'right',
        headerAlign: 'right',
        minWidth: 140,
        valueGetter: (params) => Number(params.row.amount),
        renderCell: (params) => {
            const amount = calculatePositionSizeWithPosition(params.row);
            return formatCurrencyAmount(amount, baseAssetOverride(params.row.baseAsset));
        }
    },
    collateral: {
        flex: 1,
        field: 'collateral', headerName: 'Collateral',
        align: 'right',
        headerAlign: 'right',
        minWidth: 110,
        valueGetter: (params) => Number(params.row.amount),
        renderCell: (params) => formatCurrencyAmount(Number(params.row.amount) / 10 ** tokenDecimals.usdc, 'USDC')

    },
    tpPrice: {
        flex: 1,
        field: 'tpPrice',
        headerName: 'TP',
        align: 'right',
        headerAlign: 'right',
        minWidth: 115,
        valueGetter: (params) => formatPriceWithSign(params.row.tpPrice, '$', 8)
    },
    slPrice: {
        flex: 1,
        field: 'slPrice',
        headerName: 'SL',
        align: 'right',
        headerAlign: 'right',
        minWidth: 115,
        valueGetter: (params) => formatPriceWithSign(params.row.slPrice, '$', 8)
    },
    initialPrice: {
        flex: 1,
        field: 'initialPrice',
        headerName: 'Open Price',
        align: 'right',
        headerAlign: 'right',
        minWidth: 115,
        valueGetter: (params) => formatPriceWithSign(params.row.initialPrice, '$')
    }
};


export function getOpenPositionsColumns({ editPosition, closePosition }: any): GridColDef<IPositionResponse & {
    feeAmount?: string;
    profit?: string,
    lastPrice?: string
}>[] {

    return [
        columnConfig.market,
        {

            flex: 1,
            field: 'netPnL',
            headerName: 'Net PnL',
            align: 'right',
            headerAlign: 'right',
            minWidth: 120,
            valueGetter: (params) => Number(params.row.netPnlAmount),
            renderCell: (params) => {
                return (<Observer>{() => {
                    return <span
                        className={Number(params.row.netPnlAmount) > 0 ? 'text-right nereus-color-status-up' : 'text-right nereus-color-status-down'}>{Number(getNetPnlWithPosition(params.row)) ? formatCurrencyAmount(getNetPnlWithPosition(params.row), 'USDC', 2) : <StatusIcon status="disabled-pending" />}</span>
                }}
                </Observer>);
            }
        },
        columnConfig.initialPrice,
        {
            flex: 1,
            field: 'currentPrice',
            headerName: 'Current Price',
            align: 'right',
            headerAlign: 'right',
            minWidth: 110,
            renderCell: (params) =>
                <Observer>{() => <>{params.row.lastPrice ? formatCurrencyAmount(params.row.lastPrice, '$') : ''}</>}</Observer>
        },
        columnConfig.tpPrice,
        columnConfig.slPrice,
        columnConfig.startedAt,
        columnConfig.status,
        columnConfig.amount,
        columnConfig.collateral,
        {

            flex: 1,
            field: 'feeAmount',
            headerName: 'Fee',
            align: 'right',
            headerAlign: 'right',
            minWidth: 100,
            renderCell: (params) =>
                <Observer>{() => <span className="nereus-color-status-down">{params.row.feeAmount ? formatCurrencyAmount(params.row.feeAmount, 'USDC', 2) : ''}</span>}</Observer>
        },

        {

            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            minWidth: 120,
            renderCell: (params) => {
                return (<>
                    <NereusButton
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => closePosition(params.row.id)}
                    >
                        Close
                    </NereusButton>
                    <NereusButton
                        className="UserPositions__editBtn"
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => editPosition(params.row)}
                    >
                        <EditIcon/>
                    </NereusButton>
                </>);
            }
        }
    ];
}

export function getHistoryPositionsColumns(): GridColDef<IPositionResponse>[] {

    return [
        columnConfig.market,
        {
            flex: 1,
            field: 'realizedPnL',
            headerName: 'Net PnL',
            align: 'right',
            headerAlign: 'right',
            minWidth: 120,
            valueGetter: (params) => Number(params.row.realizedPnL),
            renderCell: (params) => <span
                className={params.row.realizedPnL !== undefined && Number(params.row.realizedPnL) > 0 ? 'nereus-color-status-up' : 'nereus-color-status-down'}>
                {params.row.realizedPnL !== undefined ? formatCurrencyAmount(getNetPnlWithPosition(params.row), 'USDC', 2) : <StatusIcon status="disabled-pending" />}
            </span>
        },
        columnConfig.initialPrice,
        {
            flex: 1,
            field: 'closedPrice', headerName: 'Closed Price', minWidth: 100,
            align: 'right',
            headerAlign: 'right',
            valueGetter: (params) => params.row.closedPrice !== undefined ? formatCurrencyAmount(params.row.closedPrice, '$') : ''
        },
        columnConfig.startedAt,
        {
            flex: 1,
            field: 'finishedAt', headerName: 'Close Date', minWidth: 100,
            valueGetter: (params) => dayjs(params.row.finishedAt).valueOf(),
            renderCell: params => params.row.finishedAt !== undefined && params.row.finishedAt !== null && renderDateTime(params.row.finishedAt)
        },
        columnConfig.status,
        columnConfig.amount,
        columnConfig.collateral,
        {

            flex: 1,
            field: 'fee',
            headerName: 'Fee',
            minWidth: 120,
            align: 'right',
            valueGetter: (params) => Number(params.row.fee),
            headerAlign: 'right',
            renderCell: (params) =>
                <Observer>{() => <span className="nereus-color-status-down">{params.row.fee !== undefined && params.row.fee !== null ? formatCurrencyAmount(Number(params.row.fee) / 10 ** tokenDecimals.usdc, 'USDC', 2) : ''}</span>}</Observer>
        },
        {
            flex: 1,
            field: 'closedReason',
            headerName: 'Close Reason',
            minWidth: 70,
            valueGetter: (params: {
                row: IPositionResponse
            }) => params.row.closedReason !== undefined && closedReasonFormat[params.row.closedReason]
                ? closedReasonFormat[params.row.closedReason]
                : params.row.closedReason
        }
    ];
}